/**
 * Settings nav (Figma 09 · Settings / Nav · Scoped): You, the workspace with
 * its switcher, the brand with its switcher. 208 wide on the desk; the compact
 * shell uses the All settings index instead (AllSettingsIndex).
 */
import { useEffect, useState } from "react";
import { NavLink } from "react-router";
import { DialogTrigger, Pressable } from "react-aria-components";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/utils/helpers";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useSwitchOrganization } from "@/app/routes/dashboard/settings/api/useOrganization";
import { useSocialConnections } from "@/app/routes/dashboard/settings/api/useSocialConnections";
import {
  SETTINGS_NAV,
  canOpenPage,
  type SettingsNavSection,
  type SettingsPageId,
} from "@/app/routes/dashboard/settings/settingsNav";
import type { OrganizationRole } from "@/types/organization.type";
import { KitPopover } from "../kit/Popover";
import { kitDropdownMenuItemActiveClass, kitDropdownMenuItemClass } from "../kit/dropdownStyles";
import { PersonMark, ScopeTile } from "./parts/SettingsParts";
import { onOpenOrgPicker } from "./parts/scope";
import { BrandSwitcher, SWITCHER_PILL as PILL } from "./parts/BrandSwitcher";

function ScopeLabel({ children }: { children: string }) {
  return <p className="text-[11px] font-semibold uppercase tracking-[1.5px] text-co-faint">{children}</p>;
}

function OrgSwitcher() {
  const { user, selectedOrganization } = useCurrentUser();
  const switchOrganization = useSwitchOrganization();
  const [isOpen, setIsOpen] = useState(false);
  const organizations = user?.userOrganizations ?? [];

  useEffect(() => onOpenOrgPicker(() => setIsOpen(true)), []);

  const selected = selectedOrganization ?? organizations[0];
  if (!selected) return null;

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
      <Pressable>
        <button type="button" className={PILL} aria-label={`Workspace: ${selected.organization.name}. Switch workspace`}>
          <ScopeTile name={selected.organization.name} size={22} />
          <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-co-text">
            {selected.organization.name}
          </span>
          <ChevronsUpDown aria-hidden className="size-3.5 shrink-0 text-co-muted" />
        </button>
      </Pressable>
      <KitPopover placement="bottom start" offset={8} className="max-h-[360px] min-w-[var(--trigger-width)] overflow-y-auto p-1.5">
        <ul className="flex flex-col gap-0.5">
          {organizations.map((uo) => {
            const active = uo.organization.id === selected.organization.id;
            return (
              <li key={uo.organization.id}>
                <button
                  type="button"
                  onClick={() => {
                    switchOrganization(uo.organization.id);
                    setIsOpen(false);
                  }}
                  className={cn(kitDropdownMenuItemClass, active && kitDropdownMenuItemActiveClass)}
                >
                  <span className="truncate">
                    {uo.organization.name}
                    <span className="font-normal text-co-faint"> · {uo.role === "Guest" ? "Client" : uo.role}</span>
                  </span>
                  {active && <Check className="size-3.5 shrink-0 text-co-accent" />}
                </button>
              </li>
            );
          })}
        </ul>
      </KitPopover>
    </DialogTrigger>
  );
}

/** Reveal order on open: each section head and item appears 30ms after the one above. */
const stagger = (i: number) => ({ "--i": Math.min(i, 24) }) as React.CSSProperties;

function NavItem({ id, label, active, badge, index }: { id: SettingsPageId; label: string; active: boolean; badge?: number; index: number }) {
  return (
    <NavLink
      to={`.?page=${id}`}
      aria-current={active ? "page" : undefined}
      style={stagger(index)}
      className={cn(
        "co-notif-in unstyled-link flex h-[34px] items-center gap-2 rounded-[8px] px-3 text-[13px] transition-colors",
        active ? "bg-co-hover font-semibold text-co-text" : "font-medium text-co-text2 hover:bg-co-hover hover:text-co-text",
      )}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge ? <span className="text-[12px] font-semibold text-co-accent">{badge}</span> : null}
    </NavLink>
  );
}

export function visibleSections(role: OrganizationRole | null): SettingsNavSection[] {
  return SETTINGS_NAV.map((section) => ({
    ...section,
    items: section.items.filter((item) => canOpenPage(item.id, role)),
  })).filter((section) => section.items.length > 0);
}

/** Connections waiting on a reconnect, for the Social accounts badge. */
export function useAttentionCount(): number {
  const selectedBrandId = useWorkspaceStore((s) => s.selectedBrandId);
  const { data } = useSocialConnections(selectedBrandId);
  return (data ?? []).filter((c) => c.status === "NEEDS_REAUTH").length;
}

export default function SettingsNav({ activePage }: { activePage: SettingsPageId }) {
  const { user, selectedOrganization } = useCurrentUser();
  const role = selectedOrganization?.role ?? null;
  const attention = useAttentionCount();
  const sections = visibleSections(role);

  return (
    <nav aria-label="Settings" className="flex w-[208px] shrink-0 flex-col gap-6 self-start">
      {sections.map((section, si) => {
        const start = sections.slice(0, si).reduce((n, p) => n + 1 + p.items.length, 0);
        return (
        <div key={section.scope} className="flex flex-col gap-0.5">
          <div className="co-notif-in flex flex-col gap-2 pb-1" style={stagger(start)}>
            <ScopeLabel>{section.title}</ScopeLabel>
            {section.scope === "you" ? (
              <div className="flex h-9 items-center gap-2 pr-3">
                <PersonMark name={user?.name || user?.email} picture={user?.profilePicture} size={22} />
                <span className="truncate text-[13px] font-semibold text-co-text">{user?.name || user?.email}</span>
              </div>
            ) : section.scope === "workspace" ? (
              <OrgSwitcher />
            ) : (
              <BrandSwitcher listen />
            )}
          </div>
          {section.items.map((item, ii) => (
            <NavItem
              key={item.id}
              id={item.id}
              label={item.label}
              active={item.id === activePage}
              badge={item.id === "social-accounts" ? attention : undefined}
              index={start + 1 + ii}
            />
          ))}
        </div>
        );
      })}
    </nav>
  );
}
